import * as DialogPrimitive from "@radix-ui/react-dialog";
import classNames from "classnames";
import {
  action,
  comparer,
  computed,
  observable,
  runInAction,
  toJS,
} from "mobx";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Command } from "../lib/cmdk";
import {
  Pane,
  PaneType,
  PaneTypeNames,
  PaneViewMode,
  Position,
} from "../Types";
import { DialogOverlay, DialogPortal, DialogRoot } from "../ui/Dialog";
import { throttle } from "../Utils";
import {
  activePaneStates,
  canvasAtom,
  canvasViewport,
  canvasViewportShouldAnimateAtom,
} from "./CanvasState";
import { EvalPaneTemplates } from "./EvalPaneTemplates";
import { FocusPaneEmitter } from "./FocusPane";
import { zoomToFit } from "./ToolbarZoomButton";

export const isCommandBarOpenBox = observable.box(false);

function getPaneName(pane: Pane) {
  if (pane.name !== undefined) {
    return pane.name;
  }
  switch (pane.type) {
    case PaneType.Import:
      return pane.module?.[1];
    default:
      return undefined;
  }
}

function getPaneSecondaryText(pane: Pane | undefined) {
  if (pane === undefined) {
    return undefined;
  }
  switch (pane.type) {
    case PaneType.Evaluate:
    case PaneType.EvaluateGlobal:
      return pane.expression.substring(0, 128);
    case PaneType.State:
      return pane.initialExpression;
    case PaneType.EvaluateTemplate:
      return EvalPaneTemplates[pane.templateId].schema.title;
    case PaneType.EnvironmentVariable:
      return pane.key;
    default:
      return undefined;
  }
}

const CommandBarPaneItem = observer(
  ({ paneId, onSelect }: { paneId: string; onSelect: () => void }) => {
    const pane = computed(() =>
      canvasAtom.value.panes.find((p) => p.id === paneId)
    ).get();
    if (pane === undefined) {
      return null;
    }
    const paneName = getPaneName(pane);
    const paneNameText = paneName ?? `unnamed`;
    const secondaryText = getPaneSecondaryText(pane);

    return (
      <Command.Item
        id={paneId}
        value={paneNameText}
        onSelect={() => {
          onSelect();
          zoomToFit([paneId]);
          FocusPaneEmitter.emit("focus", { paneId, noScroll: true });
        }}
        className="flex h-8 items-center px-4 text-sm gap-3 aria-selected:bg-neutral-100 dark:aria-selected:bg-neutral-0 border-l-2 border-transparent aria-selected:border-accent-300 transition cursor-pointer whitespace-nowrap"
      >
        <span
          className={classNames("font-medium", {
            "text-neutral-500": paneName === undefined,
          })}
        >
          {paneNameText}
        </span>
        {secondaryText !== undefined ? (
          <span className="text-neutral-400 overflow-hidden text-ellipsis">
            {secondaryText}
          </span>
        ) : null}
        <span className="grow" />
        <span className="font-medium text-xs text-neutral-400">
          {PaneTypeNames[pane.type]}
        </span>
      </Command.Item>
    );
  }
);

export function toggleCommandBar() {
  runInAction(() => {
    isCommandBarOpenBox.set(!isCommandBarOpenBox.get());
  });
}

const CommandBarDialogContent = observer(() => {
  const canvasViewportOnOpenRef = useRef<
    { offset: Position; zoom: number } | undefined
  >(undefined);
  const [throttleMoveCanvas] = useState(() =>
    throttle((paneId) => {
      zoomToFit([paneId]);
    }, 300)
  );
  useEffect(() => {
    canvasViewportOnOpenRef.current = toJS(canvasViewport);
    return () => throttleMoveCanvas.cancel();
  }, []);
  const cancel = useCallback(() => {
    runInAction(() => {
      isCommandBarOpenBox.set(false);
      const canvasViewportOnOpen = canvasViewportOnOpenRef.current;
      if (canvasViewportOnOpen !== undefined) {
        canvasViewportShouldAnimateAtom.value = true;
        canvasViewport.offset = canvasViewportOnOpen.offset;
        canvasViewport.zoom = canvasViewportOnOpen.zoom;
      }
    });
  }, []);

  const sortedPaneIds = computed(
    () =>
      [...canvasAtom.value.panes]
        .filter(
          (p) =>
            activePaneStates.get(p.id)?.viewMode.get() !== PaneViewMode.Hidden
        )
        .sort((a, b) => {
          if (a.name === undefined) {
            return b.name === undefined ? 0 : 1;
          }
          if (b.name === undefined) {
            return -1;
          }
          return a.name.localeCompare(b.name);
        })
        .map((p) => p.id),
    { equals: comparer.structural }
  ).get();

  return (
    <div className="bg-neutral-0 dark:bg-neutral-150 rounded-lg shadow-lg dialog-animate-in overflow-hidden">
      <Command
        onSelectChange={(paneId) => {
          throttleMoveCanvas(paneId);
        }}
      >
        <Command.Input
          onKeyDown={(e) => {
            switch (e.key) {
              case "Escape": {
                cancel();
                break;
              }
            }
          }}
          autoFocus={true}
          placeholder="search for a pane..."
          className="w-full p-4 text-lg bg-transparent border-b border-neutral-150 placeholder:text-neutral-300 dark:placeholder:text-neutral-400 dark:border-neutral-200 mb-2 no-focus-visible"
        />
        <Command.List className="overflow-auto transition-all cmdk-list">
          <Command.Empty className="py-8 text-neutral-400 dark:text-neutral-500 flex justify-center text-sm">
            No results found
          </Command.Empty>
          <Command.Group
            heading={
              <div className="text-xs text-neutral-300 dark:text-neutral-400 pb-2 px-4">
                panes
              </div>
            }
            className="pb-2"
          >
            {sortedPaneIds.map((paneId) => (
              <CommandBarPaneItem
                paneId={paneId}
                onSelect={action(() => {
                  isCommandBarOpenBox.set(false);
                })}
                key={paneId}
              />
            ))}
          </Command.Group>
        </Command.List>
      </Command>
    </div>
  );
});

export const CommandBar = observer(() => {
  const open = isCommandBarOpenBox.get();
  const setOpen = useCallback((open: boolean) => {
    isCommandBarOpenBox.set(open);
  }, []);

  return (
    <DialogRoot open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content className="fixed left-1/2 -translate-x-1/2 top-[15vh] max-w-screen-sm w-[90vw]">
          <CommandBarDialogContent />
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogRoot>
  );
});
